import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { PackagePlus, Plus, RefreshCw } from 'lucide-react';
import { api, unwrap } from '@/api/client';
import { Badge } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog';
import { DataTable, EmptyRow, Field, RootError, Section, Textarea } from '@/components/ui/form-controls';
import { Input, NativeSelect } from '@/components/ui/input';
import { applyProblemToForm } from '@/lib/forms';
import { tenantKeys, useWhatsappTemplates } from './api';
import { groupTemplates, statusVariant, TEMPLATE_PACKS } from './helpers';
import { SUPPORTED_LOCALES, templateParamCount, templateSchema, type TemplateForm } from './schemas';

export function TemplatesPanel({ tenantId, locales }: { tenantId: string; locales: string[] }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const templates = useWhatsappTemplates(tenantId, true);
  const [importOpen, setImportOpen] = useState(false);
  const [submitOpen, setSubmitOpen] = useState(false);
  const langs = locales.length > 0 ? locales : ['de'];

  const sync = useMutation({
    mutationFn: async () => unwrap(await api.POST('/v1/tenants/{id}/channels/whatsapp/templates/sync', { params: { path: { id: tenantId } } })),
    onSuccess: (data) => {
      qc.setQueryData(tenantKeys.part(tenantId, 'wa-templates'), data);
      toast.success(t('admin.templates.synced'));
    },
  });

  const rows = groupTemplates(templates.data?.items ?? []);

  return (
    <Section
      title={t('admin.templates.title')}
      description={t('admin.templates.description')}
      actions={
        <>
          <Button size="sm" variant="outline" onClick={() => sync.mutate()} disabled={sync.isPending}>
            <RefreshCw /> {t('admin.templates.sync')}
          </Button>
          <Button size="sm" variant="outline" onClick={() => setImportOpen(true)}>
            <PackagePlus /> {t('admin.templates.importPack')}
          </Button>
          <Button size="sm" onClick={() => setSubmitOpen(true)}>
            <Plus /> {t('admin.templates.submit')}
          </Button>
        </>
      }
    >
      <DataTable head={[t('admin.templates.name'), t('admin.templates.pack'), ...langs.map((l) => l.toUpperCase())]}>
        {rows.length === 0 && <EmptyRow colSpan={2 + langs.length}>{t('admin.templates.empty')}</EmptyRow>}
        {rows.map((r) => (
          <tr key={r.name}>
            <td className="font-mono text-xs">{r.name}</td>
            <td className="text-xs text-muted-foreground">{r.pack ?? '—'}</td>
            {langs.map((l) => {
              const tpl = r.byLanguage[l];
              return (
                <td key={l} data-testid={`tpl-${r.name}-${l}`}>
                  {tpl ? (
                    <Badge variant={statusVariant(tpl.status)} title={tpl.rejectedReason ?? undefined}>
                      {t(`admin.templateStatus.${tpl.status}`)}
                    </Badge>
                  ) : (
                    <span className="text-xs text-muted-foreground">{t('admin.templates.missing')}</span>
                  )}
                </td>
              );
            })}
          </tr>
        ))}
      </DataTable>
      {importOpen && <ImportPackDialog tenantId={tenantId} locales={langs} onClose={() => setImportOpen(false)} />}
      {submitOpen && <SubmitTemplateDialog tenantId={tenantId} onClose={() => setSubmitOpen(false)} />}
    </Section>
  );
}

function ImportPackDialog({ tenantId, locales, onClose }: { tenantId: string; locales: string[]; onClose: () => void }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [pack, setPack] = useState<string>(TEMPLATE_PACKS[0]);
  const [languages, setLanguages] = useState<string[]>(locales);
  const run = useMutation({
    mutationFn: async () =>
      unwrap(await api.POST('/v1/tenants/{id}/channels/whatsapp/templates/import', { params: { path: { id: tenantId } }, body: { pack, languages } })),
    onSuccess: async () => qc.invalidateQueries({ queryKey: tenantKeys.part(tenantId, 'wa-templates') }),
  });
  const r = run.data;
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogTitle>{t('admin.templates.importPack')}</DialogTitle>
        <DialogDescription>{t('admin.templates.importHint')}</DialogDescription>
        <div className="mt-3 flex flex-col gap-3">
          <Field label={t('admin.templates.pack')} htmlFor="pack">
            <NativeSelect id="pack" value={pack} onChange={(e) => setPack(e.target.value)}>
              {TEMPLATE_PACKS.map((p) => (
                <option key={p} value={p}>
                  {p}
                </option>
              ))}
            </NativeSelect>
          </Field>
          <fieldset className="flex flex-wrap gap-3">
            <legend className="mb-1 text-xs font-medium">{t('admin.templates.languages')}</legend>
            {SUPPORTED_LOCALES.map((l) => (
              <label key={l} className="flex items-center gap-1 text-sm">
                <input
                  type="checkbox"
                  checked={languages.includes(l)}
                  onChange={(e) => setLanguages((cur) => (e.target.checked ? [...cur, l] : cur.filter((x) => x !== l)))}
                />
                {l.toUpperCase()}
              </label>
            ))}
          </fieldset>
          {r && (
            <div role="status" className="rounded-md border p-2 text-xs">
              <div>{t('admin.templates.importResult', { submitted: r.submitted.length, skipped: r.skipped.length, failed: r.failed.length })}</div>
              {r.failed.map((f) => (
                <div key={f.template} className="text-destructive">
                  {f.template}: {f.error}
                </div>
              ))}
            </div>
          )}
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={onClose}>
              {t('admin.common.close')}
            </Button>
            <Button onClick={() => run.mutate()} disabled={run.isPending || languages.length === 0}>
              {t('admin.templates.import')}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function lines(text: string): string[] {
  return text
    .split('\n')
    .map((s) => s.trim())
    .filter(Boolean);
}

function SubmitTemplateDialog({ tenantId, onClose }: { tenantId: string; onClose: () => void }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const form = useForm<TemplateForm>({
    resolver: zodResolver(templateSchema(t)),
    defaultValues: { name: '', language: 'de', bodyText: '', examples: '', quickReplies: '', footer: '' },
  });
  const body = form.watch('bodyText');
  const params = templateParamCount(body);
  const submit = useMutation({
    meta: { silent: true },
    mutationFn: async (v: TemplateForm) =>
      unwrap(
        await api.POST('/v1/tenants/{id}/channels/whatsapp/templates', {
          params: { path: { id: tenantId } },
          body: {
            name: v.name,
            language: v.language,
            // Agent templates are always UTILITY (05 §2.4); the backend enforces it as well.
            category: 'UTILITY',
            components: [],
            bodyText: v.bodyText,
            examples: lines(v.examples),
            quickReplies: lines(v.quickReplies),
            header: null,
            footer: v.footer || null,
          },
        }),
      ),
  });
  const onSubmit = form.handleSubmit(async (v) => {
    if (!params.contiguous) {
      form.setError('bodyText', { message: t('admin.validation.paramsContiguous', { p1: '{{1}}', p2: '{{2}}' }) });
      return;
    }
    if (lines(v.examples).length !== params.count) {
      form.setError('examples', { message: t('admin.validation.examplesCount', { count: params.count }) });
      return;
    }
    try {
      await submit.mutateAsync(v);
      await qc.invalidateQueries({ queryKey: tenantKeys.part(tenantId, 'wa-templates') });
      toast.success(t('admin.templates.submitted'));
      onClose();
    } catch (err) {
      applyProblemToForm(form, err, { fields: ['name', 'language', 'bodyText', 'examples', 'quickReplies', 'footer'] });
    }
  });
  const { errors, isSubmitting } = form.formState;
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogTitle>{t('admin.templates.submit')}</DialogTitle>
        <DialogDescription>{t('admin.templates.submitHint', { p1: '{{1}}', p2: '{{2}}' })}</DialogDescription>
        <form className="mt-3 flex flex-col gap-3" onSubmit={(e) => void onSubmit(e)} noValidate>
          <div className="grid grid-cols-3 gap-3">
            <Field className="col-span-2" label={t('admin.templates.name')} htmlFor="tpl-name" error={errors.name?.message}>
              <Input id="tpl-name" className="font-mono" aria-invalid={!!errors.name} {...form.register('name')} />
            </Field>
            <Field label={t('admin.templates.language')} htmlFor="tpl-lang">
              <NativeSelect id="tpl-lang" {...form.register('language')}>
                {SUPPORTED_LOCALES.map((l) => (
                  <option key={l} value={l}>
                    {l.toUpperCase()}
                  </option>
                ))}
              </NativeSelect>
            </Field>
          </div>
          <Field label={t('admin.templates.body')} htmlFor="tpl-body" error={errors.bodyText?.message} hint={t('admin.templates.bodyHint', { count: params.count })}>
            <Textarea id="tpl-body" rows={4} aria-invalid={!!errors.bodyText} {...form.register('bodyText')} />
          </Field>
          <Field label={t('admin.templates.examples')} htmlFor="tpl-ex" error={errors.examples?.message} hint={t('admin.templates.examplesHint')}>
            <Textarea id="tpl-ex" rows={2} aria-invalid={!!errors.examples} {...form.register('examples')} />
          </Field>
          <Field label={t('admin.templates.quickReplies')} htmlFor="tpl-qr" hint={t('admin.templates.quickRepliesHint')}>
            <Textarea id="tpl-qr" rows={2} {...form.register('quickReplies')} />
          </Field>
          <Field label={t('admin.templates.footer')} htmlFor="tpl-footer" error={errors.footer?.message}>
            <Input id="tpl-footer" {...form.register('footer')} />
          </Field>
          <RootError message={errors.root?.message} />
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={onClose}>
              {t('admin.common.cancel')}
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {t('admin.templates.submit')}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
